// Applies the light/dark mode and color theme to <html>, and tells canvas-drawn views (the
// network and the minimap) when colors change so they can redraw.

import { useSyncExternalStore } from 'react';
import type { Settings } from '../model/types';
import { DEFAULT_THEME_ID, findTheme, flagTokens, forcedDark, paletteOf, paletteTokens, stripeGradient, stripeRing } from './themes';
import { useApp } from './store';

const media = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function subscribeSystem(cb: () => void) {
  media?.addEventListener('change', cb);
  return () => media?.removeEventListener('change', cb);
}

export function useSystemDark(): boolean {
  return useSyncExternalStore(subscribeSystem, () => !!media?.matches, () => false);
}

/** Is the page dark? A one-way theme (Dracula) decides; otherwise the mode, then the OS. */
export function isDark(settings: Pick<Settings, 'theme' | 'themeId'>, systemDark: boolean): boolean {
  const forced = forcedDark(findTheme(settings.themeId));
  if (forced !== null) return forced;
  return settings.theme === 'dark' || (settings.theme === 'system' && systemDark);
}

/** Changes whenever the page's colors do. Canvas views list it as a redraw dependency. */
export function useColorKey(): string {
  const mode = useApp((s) => s.settings.theme);
  const themeId = useApp((s) => s.settings.themeId);
  const systemDark = useSystemDark();
  return `${themeId}:${isDark({ theme: mode, themeId }, systemDark)}`;
}

let applied: string[] = [];
const themeColorMeta = typeof document !== 'undefined' ? document.querySelector<HTMLMetaElement>('meta[name="theme-color"]') : null;
const originalThemeColor = themeColorMeta?.content;

export function applyAppearance(settings: Pick<Settings, 'theme' | 'themeId'>, systemDark: boolean) {
  const root = document.documentElement;
  // Switch colors at once; otherwise every button fades between themes for a moment.
  root.classList.add('theme-switching');
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('theme-switching')));
  const theme = findTheme(settings.themeId);
  const forced = forcedDark(theme);
  const effective = forced === null ? settings.theme : forced ? 'dark' : 'light';
  if (effective === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', effective);

  for (const name of applied) root.style.removeProperty(name);
  applied = [];
  if (theme.id !== DEFAULT_THEME_ID) {
    const dark = isDark(settings, systemDark);
    const tokens = paletteTokens(paletteOf(theme, dark));
    for (const [name, value] of Object.entries(tokens)) {
      root.style.setProperty(name, value);
      applied.push(name);
    }
    root.style.setProperty('color-scheme', dark ? 'dark' : 'light');
    applied.push('color-scheme');
    if (themeColorMeta) themeColorMeta.content = tokens['--accent'];
  } else if (themeColorMeta && originalThemeColor) themeColorMeta.content = originalThemeColor;

  // Pride themes: the flag's stripes and its colors, used for accents throughout styles.css.
  if (theme.stripes) {
    const tokens = {
      '--theme-stripes': stripeGradient(theme.stripes),
      '--theme-stripes-down': stripeGradient(theme.stripes, '180deg'),
      '--theme-ring': stripeRing(theme.stripes),
      ...flagTokens(theme.stripes, paletteOf(theme, isDark(settings, systemDark))),
    };
    for (const [name, value] of Object.entries(tokens)) {
      root.style.setProperty(name, value);
      applied.push(name);
    }
    root.classList.add('has-stripes');
  } else root.classList.remove('has-stripes');
}
